import React from 'react';
import styled from 'styled-components';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto; /* Center the container horizontally */
  padding: 40px; /* Increased padding for better spacing */
  font-family: 'Inter', Arial, sans-serif; /* Using Inter font as per instructions */
  line-height: 1.8; /* Improved line height for readability */
  color: ${({ theme }) => theme.text || '#333'}; /* Dynamic text color from theme, with fallback */
  background-color: ${({ theme }) => theme.background || '#ffffff'}; /* Dynamic background color from theme, with fallback */
  border-radius: 12px; /* More pronounced rounded corners */
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1); /* Stronger, softer shadow */
  transition: margin-left 0.2s ease-in-out; /* Smooth transition */

  @media (max-width: 768px) {
    margin-left: 70px; /* Adjust for collapsed sidebar on smaller screens */
    padding: 20px; /* Reduced padding on smaller screens */
  }
`;

const Section = styled.section`
  margin-bottom: 40px; /* Increased spacing between sections */
`;

const Heading = styled.h1`
  font-size: 1.8rem; /* Larger main heading */
  font-weight: 600; /* Extra bold */
  color: ${({ theme }) => theme.primary || '#2c3e50'}; 
  margin-bottom: 30px;
  text-align: center;
  line-height: 1.2;
`;

const SubHeading = styled.h2`
  font-size: 2rem; /* Larger subheadings */
  font-weight: 700;
  color: ${({ theme }) => theme.secondary || '#34495e'}; /* Secondary theme color for subheadings */
  margin-top: 30px;
  margin-bottom: 15px;
  border-bottom: 2px solid ${({ theme }) => theme.borderColor || '#e0e0e0'}; /* Subtle underline */
  padding-bottom: 8px;
`;

const Paragraph = styled.p`
  margin-bottom: 15px;
  color: ${({ theme }) => theme.text || '#555'};
`;

const List = styled.ul`
  list-style-type: disc;
  margin-left: 30px; /* Deeper indentation for nested lists */
  margin-bottom: 20px;
  line-height: 1.7;
  color: ${({ theme }) => theme.text || '#555'};
`;

const OrderedList = styled.ol`
  list-style-type: decimal;
  margin-left: 30px; /* Deeper indentation for nested lists */
  margin-bottom: 20px;
  line-height: 1.7;
  color: ${({ theme }) => theme.text || '#555'};
`;

const ListItem = styled.li`
  margin-bottom: 8px;
  color: ${({ theme }) => theme.text || '#555'};
`;

const Strong = styled.strong`
  font-weight: bold;
  color: ${({ theme }) => theme.strongText || '#2c3e50'}; /* Stronger text color */
`;

const CodeBlock = styled.pre`
  background-color: ${({ theme }) => theme.codeBackground || '#f8f8f8'}; /* Code background from theme */
  color: ${({ theme }) => theme.codeText || '#333'}; /* Code text color from theme */
  padding: 1.2em; /* More padding */
  border-left: 5px solid ${({ theme }) => theme.accent || '#007bff'}; /* Accent color border */
  margin: 1.5em 0; /* More margin */
  font-family: 'Consolas', 'Monaco', monospace;
  white-space: pre-wrap; /* Ensures code wraps */
  overflow-x: auto;
  border-radius: 6px; /* Rounded corners for code blocks */
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.05); /* Subtle inner shadow */
`;

const InlineCode = styled.code`
  background-color: ${({ theme }) => theme.inlineCodeBackground || '#e8e8e8'};
  color: ${({ theme }) => theme.inlineCodeText || '#c0392b'}; /* Highlight color for inline code */
  padding: 0.2em 0.4em;
  border-radius: 4px;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 0.9em;
`;


const Abhs = () => {
  return (
    <Container>
      <Heading>Data Preparation Guide for Production Lot Uploads: Alberta Health Solutions(ABHS)</Heading>
      <Paragraph>Vet: CSBV00003(3)</Paragraph>
      <Paragraph>The site has two (2) herds.</Paragraph>
        <List>
          <ListItem>CSBH0000028</ListItem>
          <ListItem>CSBH0000029</ListItem>
        </List>
     <Paragraph>Project Years: 2019-2024 </Paragraph>
      <Paragraph>
        This guide outlines the steps and considerations for preparing production lot data for upload into the system, focusing on a retrospective data format from a hypothetical research year (e.g., 2019 to 2024). These guidelines are designed to help ensure data quality and successful integration.
      </Paragraph>
      <Paragraph>
        For detailed examples of spreadsheet preparation, refer to models used in similar projects (e.g., V7, Herd 61 spreadsheets).
      </Paragraph>

      <Section>
        <SubHeading>General Preparation Steps for the "To Upload" Spreadsheet</SubHeading>
        <Paragraph>
          To get your data ready for upload and integration into the database, follow these steps:
        </Paragraph>
        <OrderedList>
          <ListItem>
            <Strong>Worksheet Setup:</Strong>
            <List>
              <ListItem>Rename your original source data worksheet to "Source".</ListItem>
              <ListItem>Add a new worksheet named "Work". All modifications to the data should occur here.</ListItem>
              <ListItem>Add another new worksheet for the final prepared data, using a naming convention that reflects the relevant identifiers (e.g., "Group ID Vet Code Herd Code Project Year To Upload").</ListItem>
            </List>
          </ListItem>
          <ListItem>
            <Strong>Initial Data Transfer and Formatting Cleanup:</Strong>
            <List>
              <ListItem>Copy all data from the "Source" worksheet and paste it as "Values" into the "Work" worksheet.</ListItem>
              <ListItem>Select all data (e.g., by clicking the upper-left corner) and apply the "Normal" style to remove all existing formatting.</ListItem>
              <ListItem>With all data still selected, use "Autofit Column Width" to adjust column sizes for readability.</ListItem>
            </List>
          </ListItem>
          <ListItem>
            <Strong>Add Essential Identification Columns:</Strong>
            <List>
              <ListItem>
                <Strong>Project Year:</Strong> Insert a new column A and name it "Project Year". Populate this column with the project year relevant to the data in all rows.
              </ListItem>
              <ListItem>
                <Strong>Vet Clinic Code:</Strong> Insert another new column A (it will become the new column B after "Project Year") and name it "Vet Clinic Code". Populate this column with the appropriate veterinary clinic code (e.g., <InlineCode>CSBV00003</InlineCode>) for all rows.
              </ListItem>
            </List>
          </ListItem>
          <ListItem>
            <Strong>Date Formatting:</Strong>
            <List>
              <ListItem>Locate the "In Date" column. Highlight its values and format them to a standard date format.</ListItem>
              <ListItem>Locate the "Out Date" column. Highlight its values and format them to a standard date format.</ListItem>
              <ListItem>
                <Strong>Important Note:</Strong> After reformatting date columns, you may need to rename them back to their original names for templating purposes. If the original column name is needed elsewhere, consider adding an "original" suffix to the source column name (e.g., "In Date Original").
              </ListItem>
            </List>
          </ListItem>
          <ListItem>
            <Strong>Add Weight Unit Columns:</Strong>
            <List>
              <ListItem>
                <Strong>Arrival Weight Units:</Strong> Insert a new column between "In wt (KG)" and "Hd In". Name this column "Arrival Weight Units" and insert the value "kgs" for all rows.
              </ListItem>
              <ListItem>
                <Strong>Shipping Weight Units:</Strong> Insert a new column between "Out wt (kg)" and "Out Date". Name this column "Shipping Weight Units" and insert the value "kgs" for all rows.
              </ListItem>
            </List>
          </ListItem>
          <ListItem>
            <Strong>Add Mortality/Morbidity Unit Columns:</Strong>
            <List>
              <ListItem>
                <Strong>Mortality Units:</Strong> Insert a new column after the last existing column (e.g., "Metabolic 1st pulls"). Name this column "Mortality Units" and insert the value "Number" for all rows.
              </ListItem>
              <ListItem>
                <Strong>Morbidity Units:</Strong> Insert a new column after the "Mortality Units" column. Name this column "Morbidity Units" and insert the value "Number" for all rows.
              </ListItem>
            </List>
          </ListItem>
          <ListItem>
            <Strong>Total Head Days Calculation (if missing):</Strong>
            <List>
              <ListItem>If "Total Head Days" is not present in the spreadsheet, it will need to be calculated.</ListItem>
              <ListItem>
                The calculation is: <CodeBlock>Total Head Days = Total days on feed * Total Number of Animals In</CodeBlock>.
              </ListItem>
              <ListItem>
                In the database, this translates to: <CodeBlock>csb_pl_total_head_days = csb_pl_total_days_on_feed * csb_pl_n_animals_in</CodeBlock>.
              </ListItem>
              <ListItem>Add a new column for this calculation if necessary, and populate it accordingly.</ListItem>
            </List>
          </ListItem>
        </OrderedList>
        <Paragraph>
          Your data should now be ready for a first upload and subsequent checking and integration.
        </Paragraph>
      </Section>

      <Section>
        <SubHeading>Additional Data Validation and Cleanup Considerations</SubHeading>
        <Paragraph>
          To ensure the data is fully ready for uploading, consider these additional steps:
        </Paragraph>
        <OrderedList>
          <ListItem>
            <Strong>Remove Excess Rows/Columns:</Strong>
            <List>
              <ListItem>Move to the last column containing data. Highlight approximately 10 or more columns to the right and delete them.</ListItem>
              <ListItem>Move to the last row containing data. Highlight approximately 10 or more rows below and delete them.</ListItem>
              <ListItem>This helps remove any hidden characters, stray data, or formatting issues that might exist beyond the active dataset.</ListItem>
            </List>
          </ListItem>
          <ListItem>
            <Strong>Format Numeric Columns:</Strong>
            <List>
              <ListItem>For columns containing numbers, highlight the values and format them specifically as "Number."</ListItem>
              <ListItem>While not always strictly necessary, especially if the first few rows of a number field are null, formatting can help prevent upload problems.</ListItem>
            </List>
          </ListItem>
          <ListItem>
            <Strong>Date Consistency Checks:</Strong>
            <List>
              <ListItem>Verify that "Out Date" is always on or after "In Date."</ListItem>
              <ListItem>
                You can create a temporary checking column with a calculated value: <CodeBlock>= "Out Date" - "In Date"</CodeBlock>.
              </ListItem>
              <ListItem>A result less than 0 indicates a problem.</ListItem>
              <ListItem>A very large positive value (e.g., more than 365 days) might also indicate a date entry error. This checking column can be ignored by the templating system during upload.</ListItem>
              <ListItem>You can also cross-check this calculated "days on feed" value against your spreadsheet's "Average DOF" (Days on Feed) column for additional verification.</ListItem>
            </List>
          </ListItem>
          <ListItem>
            <Strong>Percentage Value Validation:</Strong>
            <List>
              <ListItem>If a column contains percentage values, ensure that all numbers are strictly between 0 and 100.</ListItem>
            </List>
          </ListItem>
          <ListItem>
            <Strong>Animal Count Consistency Checks:</Strong>
            <List>
              <ListItem>
                Consider creating a checking column with a calculated value: <CodeBlock>= "Animals In" - "Animals Out (Shipped)"</CodeBlock>.
              </ListItem>
              <ListItem>A result less than 0 indicates a potential discrepancy that needs investigation.</ListItem>
            </List>
          </ListItem>
          <ListItem>
            <Strong>Outlier Detection for Animal Counts:</Strong>
            <List>
              <ListItem>If the number of animals shipped in a particular production lot is significantly higher (e.g., 10 or 100 times) than other lots, query the source to confirm the accuracy of the value.</ListItem>
            </List>
          </ListItem>
          <ListItem>
            <Strong>Handle Blank Cells:</Strong>
            <List>
              <ListItem>If any critical cells are blank, consult with the research or data source team to understand the reason and determine appropriate handling.</ListItem>
            </List>
          </ListItem>
        </OrderedList>
      </Section>

      <Section>
        <SubHeading>Post-Calculation Update for "To Upload" Spreadsheet</SubHeading>
        <Paragraph>
          After calculating new fields (e.g., "Total Head Days") and updating the "Work" spreadsheet:
        </Paragraph>
        <OrderedList>
          <ListItem>Navigate to your final "To Upload" spreadsheet.</ListItem>
          <ListItem>Type <InlineCode>CTRL-A</InlineCode> (or <InlineCode>Cmd-A</InlineCode> on Mac) to select all of the data.</ListItem>
          <ListItem>
            Right-click within the selected data and choose "Clear Contents" from the menu. <Strong>Avoid using "Delete," as it might retain some hidden formatting that could cause issues.</Strong>
          </ListItem>
          <ListItem>Once cleared, the sheet is ready for the updated data to be pasted or generated, and then for re-upload.</ListItem>
        </OrderedList>
        <Paragraph>
          These steps should help streamline your data preparation process and reduce common upload errors.
        </Paragraph>
      </Section>
    </Container>
  );
};

export default Abhs;
